import { PDFDocument, PDFImage, PDFPage, degrees, rgb } from 'pdf-lib';
import { blobToUint8Array } from './canvas';
import {
  computeInsideLayout,
  computeOutsideLayout,
  effectivePaper,
  type SpreadLayout,
} from './geometry';
import { buildReadmePdf } from './readmePdf';
import { DEFAULT_MARKS, PRESET_SHORT_EDGE } from './constants';
import type {
  CardSize,
  Drift,
  MarkSettings,
  Orientation,
  PaperSize,
  PrinterSettings,
  SlotImage,
  SlotMap,
  SpreadFlip,
} from '../types';

const FOLD_RGB = rgb(0.48, 0.42, 0.32);

// Convert top-left coords (geometry.ts convention) to pdf-lib's bottom-left
// origin. pdf-lib places images by their bottom-left corner.
const toPdfY = (paperHeightPx: number, topLeftY: number, height: number) =>
  paperHeightPx - topLeftY - height;

const drawSpreadDecorations = (
  page: PDFPage,
  paperHeightPx: number,
  layout: SpreadLayout,
  marks: MarkSettings
) => {
  if (marks.foldLine) {
    page.drawLine({
      start: { x: layout.foldStart.x, y: paperHeightPx - layout.foldStart.y },
      end: { x: layout.foldEnd.x, y: paperHeightPx - layout.foldEnd.y },
      thickness: 1,
      color: FOLD_RGB,
      dashArray: [6, 4],
    });
  }
  if (marks.cornerMarks) {
    for (const m of layout.cropMarks) {
      page.drawLine({
        start: { x: m.x1, y: paperHeightPx - m.y1 },
        end: { x: m.x2, y: paperHeightPx - m.y2 },
        thickness: 1,
        color: FOLD_RGB,
      });
    }
  }
  if (marks.cardBorder) {
    page.drawRectangle({
      x: layout.spreadX,
      y: toPdfY(paperHeightPx, layout.spreadY, layout.spreadH),
      width: layout.spreadW,
      height: layout.spreadH,
      borderColor: FOLD_RGB,
      borderWidth: 0.8,
      borderDashArray: [4, 4],
      opacity: 0,
      borderOpacity: 1,
    });
  }
};

interface PanelRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

const drawPanel = (
  page: PDFPage,
  paperHeightPx: number,
  rect: PanelRect,
  img: PDFImage,
  rotate180: boolean
) => {
  const pdfY = toPdfY(paperHeightPx, rect.y, rect.height);
  if (rotate180) {
    page.drawImage(img, {
      x: rect.x + rect.width,
      y: pdfY + rect.height,
      width: rect.width,
      height: rect.height,
      rotate: degrees(180),
    });
  } else {
    page.drawImage(img, {
      x: rect.x,
      y: pdfY,
      width: rect.width,
      height: rect.height,
    });
  }
};

const embedSlot = async (pdf: PDFDocument, slot: SlotImage | undefined) => {
  if (!slot) return null;
  const bytes = await blobToUint8Array(slot.blob);
  return await pdf.embedJpg(bytes);
};

const drawSpreadImages = (
  page: PDFPage,
  paperHeightPx: number,
  layout: SpreadLayout,
  canonicalFirst: PDFImage | null,
  canonicalSecond: PDFImage | null,
  flip: SpreadFlip
) => {
  // swapLR semantically means "swap across the fold." In portrait that's
  // left↔right; in landscape that's top↔bottom. The geometry abstracts the
  // axis, so this code is orientation-agnostic.
  const firstImg = flip.swapLR ? canonicalSecond : canonicalFirst;
  const secondImg = flip.swapLR ? canonicalFirst : canonicalSecond;
  if (firstImg) drawPanel(page, paperHeightPx, layout.firstPanel, firstImg, flip.rotate180);
  if (secondImg) drawPanel(page, paperHeightPx, layout.secondPanel, secondImg, flip.rotate180);
};

export interface BuildOptions {
  slots: SlotMap;
  cardSize: CardSize;
  paperSize: PaperSize;
  orientation: Orientation;
  drift: Drift;
  marks?: MarkSettings;
  printer?: PrinterSettings;
}

export const buildFoldCardPdf = async ({
  slots,
  cardSize,
  paperSize,
  orientation,
  drift,
  marks = DEFAULT_MARKS,
  printer = PRESET_SHORT_EDGE,
}: BuildOptions): Promise<Uint8Array> => {
  const pdf = await PDFDocument.create();
  pdf.setTitle('Print-and-fold card');
  pdf.setCreator('foldcardpdf');
  pdf.setProducer('foldcardpdf (pdf-lib)');

  // The PDF page is sized to the EFFECTIVE paper (landscape paper for portrait
  // cards; portrait paper for landscape cards). All geometry returned by
  // computeOutsideLayout / computeInsideLayout is already in effective coords.
  const effP = effectivePaper(paperSize, orientation);

  // ===== Page 1: outside spread =====
  const p1 = pdf.addPage([effP.widthPx, effP.heightPx]);
  const outsideLayout = computeOutsideLayout(paperSize, cardSize, orientation);

  const [backImg, frontImg] = await Promise.all([
    embedSlot(pdf, slots.back),
    embedSlot(pdf, slots.front),
  ]);

  // Canonical: back is the FIRST panel (left in portrait, top in landscape).
  // Front is the SECOND panel (right / bottom).
  drawSpreadImages(p1, effP.heightPx, outsideLayout, backImg, frontImg, printer.outside);
  drawSpreadDecorations(p1, effP.heightPx, outsideLayout, marks);

  // ===== Page 2: inside spread (only if any inside image) =====
  const hasInside = Boolean(slots.insideLeft || slots.insideRight);
  if (hasInside) {
    const p2 = pdf.addPage([effP.widthPx, effP.heightPx]);
    const insideLayout = computeInsideLayout(paperSize, cardSize, drift, orientation);

    const [insideFirstImg, insideSecondImg] = await Promise.all([
      embedSlot(pdf, slots.insideLeft),
      embedSlot(pdf, slots.insideRight),
    ]);

    // Canonical: insideLeft is the FIRST panel (left/top), insideRight is
    // the SECOND (right/bottom). The SpreadFlip settings decide whether the
    // printer's duplex flip needs swap / rotate compensation.
    drawSpreadImages(
      p2,
      effP.heightPx,
      insideLayout,
      insideFirstImg,
      insideSecondImg,
      printer.inside
    );
    drawSpreadDecorations(p2, effP.heightPx, insideLayout, marks);
  }

  // ===== Final page: README =====
  // README still uses the canonical paper dims for now — it's a text page
  // that doesn't care about card orientation.
  await buildReadmePdf(pdf, paperSize, cardSize, drift, hasInside);

  return await pdf.save();
};

export const filenameForFoldCard = (cardSize: CardSize, orientation: Orientation, ts: string) => {
  const sizePart =
    cardSize.id === 'custom' ? `${cardSize.widthIn}x${cardSize.heightIn}` : cardSize.id;
  const orientPart = orientation === 'landscape' ? '-landscape' : '';
  return `foldcard-${sizePart}${orientPart}-${ts}.pdf`;
};
